import CDialBase from './Dials'
import { PlaceInNestedAkixiNameSpace } from '../utils/Utils'
import { EaseInOutQuad } from '../../EasingFunctions'

/**
 * This class draws a new line statistic graphic.
 * @param {object} pNode The DOM element to attach the dial canvas to.
 */
class CBarGauge extends CDialBase {
    // Colour definition for blue.
    private readonly sFILL_COLOUR_BLUE = '#03a9fa'

    /**
     * Private method.
     *
     * Draws the dial on the canvas.
     *
     * @param {number} [flStopValue] Optional - The value at which the dial ends,
     * defaults to the current dial value.
     * @param {number} [flStartValue] Optional - The value at which the dial
     * starts, defaults to the stop value.
     * @param {number} [flFractionComplete=1.0] Optional - How far through the
     * animation we are, defaults to 100%.
     */
    protected Draw(
        flStopValue: number = this.m_flCurrentValue,
        flStartValue: number = flStopValue,
        flFractionComplete = 1.0
    ): void {
        const p2DContext = this.m_pCanvas.getContext('2d') as CanvasRenderingContext2D
        const flOuterLineThickness = 0.01 * Math.min(this.m_iWidth, this.m_iHeight)
        let flFontSize = 40
        const flMidHeight = this.m_iHeight / 2

        /*
         * Draw the outer dial elements
         */
        p2DContext.fillStyle = this.m_sBackgroundColour
        p2DContext.fillRect(0, 0, this.m_iWidth, this.m_iHeight)

        const flLeftEdge = this.m_iWidth * 0.15
        const iBoxWidth = this.m_iWidth - flLeftEdge * 2
        const iBoxHeight = this.m_iHeight / 2
        const iZeroHeightPos = this.m_iHeight / 2 - iBoxHeight / 2
        const flDisplayValue = EaseInOutQuad(flFractionComplete) * (flStopValue - flStartValue) + flStartValue

        /*
         * Draw the progress bar.
         */
        let flUsableFraction = flDisplayValue / this.m_flRange

        p2DContext.fillStyle = this.sFILL_COLOUR_BLUE
        p2DContext.beginPath()
        let flStartEdge = flLeftEdge
        if (this.m_bReverseDirection) {
            flUsableFraction = -flUsableFraction
            flStartEdge = flLeftEdge + iBoxWidth
        }
        p2DContext.rect(flStartEdge, iZeroHeightPos, flUsableFraction * iBoxWidth, iBoxHeight)
        p2DContext.fill()

        const flCenterXCoord = iBoxWidth / 2 + flLeftEdge
        const flQuarterXCoord = iBoxWidth * 0.25 + flLeftEdge
        const flThreeQuarterXCoord = iBoxWidth * 0.75 + flLeftEdge
        const flScoreHeight = iBoxHeight * 0.1

        p2DContext.strokeStyle = this.m_sForegroundColour
        p2DContext.lineWidth = flOuterLineThickness
        p2DContext.beginPath()
        p2DContext.rect(flLeftEdge, iZeroHeightPos, iBoxWidth, iBoxHeight)
        p2DContext.stroke()

        p2DContext.lineCap = 'butt'

        // Quarter line.
        p2DContext.beginPath()
        p2DContext.moveTo(flQuarterXCoord, iZeroHeightPos)
        p2DContext.lineTo(flQuarterXCoord, iZeroHeightPos + flScoreHeight)
        p2DContext.stroke()
        // Center line.
        p2DContext.beginPath()
        p2DContext.moveTo(flCenterXCoord, iZeroHeightPos)
        p2DContext.lineTo(flCenterXCoord, iZeroHeightPos + flScoreHeight)
        p2DContext.stroke()
        // Three quarter line.
        p2DContext.beginPath()
        p2DContext.moveTo(flThreeQuarterXCoord, iZeroHeightPos)
        p2DContext.lineTo(flThreeQuarterXCoord, iZeroHeightPos + flScoreHeight)
        p2DContext.stroke()

        // Quarter line bottom.
        p2DContext.beginPath()
        p2DContext.moveTo(flQuarterXCoord, iBoxHeight + iZeroHeightPos)
        p2DContext.lineTo(flQuarterXCoord, iBoxHeight + iZeroHeightPos - flScoreHeight)
        p2DContext.stroke()
        // Center line bottom.
        p2DContext.beginPath()
        p2DContext.moveTo(flCenterXCoord, iBoxHeight + iZeroHeightPos)
        p2DContext.lineTo(flCenterXCoord, iBoxHeight + iZeroHeightPos - flScoreHeight)
        p2DContext.stroke()
        // Three quarter line bottom.
        p2DContext.beginPath()
        p2DContext.moveTo(flThreeQuarterXCoord, iBoxHeight + iZeroHeightPos)
        p2DContext.lineTo(flThreeQuarterXCoord, iBoxHeight + iZeroHeightPos - flScoreHeight)
        p2DContext.stroke()
        /*
         * Draw the centre text.
         */
        p2DContext.fillStyle = this.m_sForegroundColour
        p2DContext.font = `${flFontSize}px ${this.m_sFontFamily}`
        p2DContext.textAlign = 'center'
        p2DContext.textBaseline = 'bottom'
        const pTextDimensions = p2DContext.measureText(this.m_sDisplayValue)

        const flWidthRatio = (this.m_iWidth * 0.5) / pTextDimensions.width
        const flHeightRatio = (this.m_iHeight * 0.5) / flFontSize

        flFontSize *= Math.min(flHeightRatio, flWidthRatio)
        p2DContext.font = `${flFontSize}px ${this.m_sFontFamily}`
        p2DContext.fillText(this.m_sDisplayValue, this.m_iWidth / 2, flMidHeight + (1.1 * flFontSize) / 2)
    }
}

export default CBarGauge
PlaceInNestedAkixiNameSpace('Dials', 'CBarGauge', CBarGauge)
